import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { formatCustomDate, formatDateToLexicographicalOrder, updateYAxisTitle } from '../AnalyticsHelper';
import { DEFAULT_HEIGHT, DEFAULT_TIMEZONE, PT2H } from '../AnalyticsConstants';
import moment from 'moment';
import CompatioLoader from 'Components/Common/components/CompatioLoader';

type DataPoint = { x: string; y: number | null; };

type SeriesData = { name: string; data: DataPoint[]; };

type DashedLineChartProps = { 
  data: SeriesData[]; 
  dashedArray?: number[]; 
  metricName?: string; 
  xAxis?: string[]; 
  granularity?: string; 
  timezone: string; 
};

const DashedLineChart = React.memo(({data, dashedArray = [], metricName = '', xAxis = [], granularity = '', timezone = DEFAULT_TIMEZONE }: DashedLineChartProps) => {
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    setIsLoading(true);
    
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 10);

    return () => clearTimeout(timer);
  }, [data, xAxis]);

  const chartOptions: any = React.useMemo(() => ({
    chart: { 
      toolbar: { show: false }, zoom: { enabled: false },
    },
    xaxis: { 
      type: 'category', 
      categories: xAxis,
      labels: { 
        show: true,
        formatter: function (value:any) {
          const date = new Date(value);
          if (granularity === PT2H) {
            return formatCustomDate(value);
          } else {
            return formatDateToLexicographicalOrder(date, timezone);
          }
        },
      }, 
    },
    stroke: {
      width: 3,
      curve: 'straight',
      dashArray: dashedArray
    },
    markers: {
      size: 5,
      hover: { size: 7 },
      discrete: []
    },
    yaxis: { 
      opposite: false, 
      title: { 
        text: updateYAxisTitle(metricName ?? ""), 
        rotate: -90, 
        offsetX: 3, 
        offsetY: 0, 
        style: { 
          color: '#8C919A', 
          fontSize: '11px', 
          fontFamily: 'Noto Sans, sans-serif', 
          fontWeight: 700, 
          cssClass: 'apexcharts-yaxis-title', 
        }, 
      }, 
    },
    dataLabels: { 
      enabled: false, 
      formatter: function (val:any) { return val + "%"; }, 
      offsetY: -20, 
      style: { fontSize: '8px', colors: ["transparent"] } 
    },
  }), [granularity, metricName, xAxis, timezone]);

  const series = React.useMemo(() => {
    return data.map((entry, index) => ({
      name: entry.name,
      data: xAxis?.map((dateString) => {
        const categoryDate = moment.utc(dateString);
        const dataPoint = entry.data.find((point) => {
          const pointDate = moment.utc(point.x);
          return pointDate.isSame(categoryDate);
        });
        return dataPoint ? dataPoint.y : null;
      }),
      strokeDashArray: index === 0 ? undefined : '5',
    }));
  }, [data, xAxis]);

  if (isLoading) {
    return (
      <div className='tw-justify-center tw-flex tw-text-center tw-items-center' style={{ height: DEFAULT_HEIGHT }}>
        <CompatioLoader />
      </div>
    );
  }

  return (
    <div style={{ height: DEFAULT_HEIGHT }}>
      <ReactApexChart
        options={chartOptions}
        series={series as any}
        type="line"
        height={DEFAULT_HEIGHT}
        width="100%"
      />
    </div>
  );
});

DashedLineChart.displayName = 'DashedLineChart';

export default DashedLineChart;